import React from 'react';
import PropTypes from 'prop-types';

import {Hidden, Skeleton, TableRow} from "@mui/material";
import {styled} from "@mui/material/styles";

import ListingTableCell from "./ListingTableCell";
import {greyishBlue50, greyishBlue100} from "assets/jss/main";

ListingRowsSkeleton.propTypes = {
    rows: PropTypes.number,
    // the variation list has no Scoring column
    withScoring: PropTypes.bool,
};

const StyledBar = styled(Skeleton, {shouldForwardProp: (prop) => 'dark' !== prop})(({dark}) => ({
    transform: 'none',
    borderRadius: '4px',
    backgroundColor: dark ? greyishBlue100 : greyishBlue50,
    // left-to-right shimmer of the wave animation, as a white shine; MUI
    // delays the wave by 0.5s — with a fast API the skeleton would be gone
    // before the first sweep even starts
    '&::after': {
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent)',
        animationDelay: '0s',
        animationDuration: '1.2s'
    }
}));

const ImageColumn = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
});

const ImageBlock = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '84px'
});

const Column = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
});

// Mirrors ListingArticleCell's fixed 224px so the columns do not jump when the real rows
// replace the skeleton.
const ArticleColumn = styled(Column)({
    gap: '12px',
    width: '224px'
});

const PillsRow = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    minWidth: '160px'
});

export const SkeletonBar = (props) => <StyledBar animation="wave" {...props}/>;

const Bar = SkeletonBar;

function SkeletonRow({withScoring}) {
    return (
        <TableRow>
            <ListingTableCell>
                <ImageColumn>
                    <Bar dark width={16} height={16}/>
                    <ImageBlock>
                        <Bar dark width={84} height={112} sx={{borderRadius: '8px'}}/>
                        <Bar width={84} height={20}/>
                    </ImageBlock>
                </ImageColumn>
            </ListingTableCell>
            <ListingTableCell>
                <ArticleColumn>
                    <Column>
                        <Bar dark height={16}/>
                        <Bar height={16}/>
                    </Column>
                    <Column>
                        <Bar height={14}/>
                        <Bar height={14}/>
                    </Column>
                </ArticleColumn>
            </ListingTableCell>
            <Hidden smDown>
                <ListingTableCell>
                    <Column sx={{width: '104px'}}>
                        <Bar dark height={16}/>
                        <Bar height={14}/>
                    </Column>
                </ListingTableCell>
            </Hidden>
            <Hidden smDown>
                <ListingTableCell>
                    <Column sx={{width: '104px'}}>
                        <Bar dark height={16}/>
                        <Bar height={14}/>
                    </Column>
                </ListingTableCell>
            </Hidden>
            {withScoring && (
                <Hidden smDown>
                    <ListingTableCell>
                        <Column sx={{width: '104px'}}>
                            <Bar dark height={20}/>
                            <Bar height={14}/>
                        </Column>
                    </ListingTableCell>
                </Hidden>
            )}
            <Hidden smDown>
                <ListingTableCell>
                    <PillsRow>
                        <Bar width={52} height={20}/>
                        <Bar width={32} height={20}/>
                        <Bar dark width={72} height={20}/>
                        <Bar dark width={80} height={20}/>
                        <Bar dark width={64} height={20}/>
                    </PillsRow>
                </ListingTableCell>
            </Hidden>
        </TableRow>
    );
}

// first-load placeholder of the product / variation lists
function ListingRowsSkeleton({rows = 5, withScoring = true}) {
    return (
        <>
            {Array.from({length: rows}, (_, index) => (
                <SkeletonRow key={index} withScoring={withScoring}/>
            ))}
        </>
    );
}

export default ListingRowsSkeleton;
